import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

/** Fixed-position coordinates for the open dropdown. */
interface MenuPosition {
  top: number;
  left: number;
}

/** Resolved presentation for a single status value. */
export interface StatusChipItem {
  value: string;
  label: string;
  dot: string;
  text: string;
  bg: string;
}

/** Hue + saturation for a status accent; lightness is applied per surface. */
interface StatusHue {
  hue: number;
  saturation: number;
}

// Standard colors for common-sense status words. Anything not listed falls
// back to a deterministic hue derived from the status string (see `hueFor`), so
// unique/custom statuses still get a stable, distinct color.
const KNOWN_STATUS_HUES: Record<string, StatusHue> = {
  // green — live / positive
  open: { hue: 152, saturation: 55 },
  active: { hue: 152, saturation: 55 },
  live: { hue: 152, saturation: 55 },
  success: { hue: 152, saturation: 55 },
  approved: { hue: 152, saturation: 55 },
  resolved: { hue: 152, saturation: 55 },
  passed: { hue: 152, saturation: 55 },
  // blue — finished / in motion
  completed: { hue: 208, saturation: 70 },
  complete: { hue: 208, saturation: 70 },
  done: { hue: 208, saturation: 70 },
  in_progress: { hue: 208, saturation: 70 },
  started: { hue: 208, saturation: 70 },
  processing: { hue: 208, saturation: 70 },
  // amber — waiting / attention
  pending: { hue: 38, saturation: 85 },
  waiting: { hue: 38, saturation: 85 },
  review: { hue: 38, saturation: 85 },
  in_review: { hue: 38, saturation: 85 },
  needs_review: { hue: 38, saturation: 85 },
  queued: { hue: 38, saturation: 85 },
  on_hold: { hue: 38, saturation: 80 },
  revise: { hue: 46, saturation: 90 },
  warning: { hue: 42, saturation: 90 },
  // red — negative / stopped
  cancelled: { hue: 2, saturation: 70 },
  canceled: { hue: 2, saturation: 70 },
  rejected: { hue: 2, saturation: 70 },
  failed: { hue: 2, saturation: 70 },
  error: { hue: 2, saturation: 70 },
  blocked: { hue: 2, saturation: 70 },
  denied: { hue: 2, saturation: 70 },
  // gray — inactive / neutral
  closed: { hue: 222, saturation: 12 },
  archived: { hue: 222, saturation: 12 },
  draft: { hue: 222, saturation: 12 },
  disregard: { hue: 222, saturation: 12 },
  inactive: { hue: 222, saturation: 12 },
  disabled: { hue: 222, saturation: 12 },
  // cyan — informational
  info: { hue: 190, saturation: 65 },
};

/** Normalizes a status value to a lookup key: lowercase, spaces/dashes → `_`. */
function normalize(value: string): string {
  return value.toLowerCase().trim().replace(/[\s-]+/g, '_');
}

/** Deterministic hue (0–359) for an unknown status, stable per string. */
function hueFor(value: string): number {
  let hash = 0;
  for (const character of value) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  return hash % 360;
}

/** Humanises a status value for display: `needs_review` → `Needs Review`. */
function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/** Builds the full presentation (label + colors) for a status value. */
function toItem(value: string): StatusChipItem {
  const known = KNOWN_STATUS_HUES[normalize(value)];
  const { hue, saturation } = known ?? { hue: hueFor(normalize(value)), saturation: 60 };
  // Comma-separated hsl() for the widest compatibility (older browsers and the
  // jsdom test environment don't parse the space-separated syntax).
  return {
    value,
    label: humanize(value),
    dot: `hsl(${hue}, ${saturation}%, 45%)`,
    text: `hsl(${hue}, ${Math.min(saturation + 8, 92)}%, 28%)`,
    bg: `hsl(${hue}, ${Math.max(saturation - 5, 18)}%, 94%)`,
  };
}

/**
 * A status chip with a dropdown to change the status. Pass the available status
 * values (e.g. enum values) and the current value; the component owns the
 * colors — standard hues for common status words, with a deterministic
 * fallback hue for any unique/custom status.
 *
 * Fully self-contained (no Material/CDK): the dropdown uses a backdrop to close
 * on outside click. The menu is `position: fixed`, anchored to the chip, so it's
 * never clipped by an ancestor's `overflow` and flips up near the viewport edge.
 *
 * @example
 * <fb-status-chip
 *   [statuses]="allStatuses"
 *   [current]="round.status"
 *   (statusChange)="onStatusChange($event)" />
 */
@Component({
  selector: 'fb-status-chip',
  templateUrl: './status-chip.html',
  styleUrl: './status-chip.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()',
    '(window:resize)': 'close()',
    '(window:scroll)': 'close()',
  },
})
export class StatusChip {
  /** Available status values to choose from (e.g. enum values). */
  readonly statuses = input<string[]>([]);

  /** The current status value. */
  readonly current = input.required<string>();

  /** Emits the chosen status value when the user picks a different one. */
  readonly statusChange = output<string>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  // Approximate row height + padding of the menu, used to decide whether to flip
  // the menu above the chip when there isn't room below.
  private static readonly MENU_ITEM_HEIGHT = 34;
  private static readonly MENU_PADDING = 8;
  private static readonly MENU_MIN_WIDTH = 181;

  protected readonly isOpen = signal(false);
  protected readonly menuPosition = signal<MenuPosition | null>(null);

  protected readonly currentItem = computed(() => toItem(this.current()));

  protected readonly items = computed<StatusChipItem[]>(() => {
    const values = this.statuses().length ? this.statuses() : [this.current()];
    return values.map(toItem);
  });

  protected toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.openMenu();
    }
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  /** Measures the chip and positions the fixed menu (flipping up if needed). */
  private openMenu(): void {
    const chip = this.elementRef.nativeElement.querySelector('.chip');
    if (!chip || typeof window === 'undefined') {
      this.isOpen.set(true);
      return;
    }
    const rect = chip.getBoundingClientRect();
    const menuHeight = this.items().length * StatusChip.MENU_ITEM_HEIGHT + StatusChip.MENU_PADDING;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < menuHeight + 8 && rect.top > spaceBelow;

    const top = flipUp ? rect.top - menuHeight - 4 : rect.bottom + 4;
    // Keep the menu within the viewport horizontally.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - StatusChip.MENU_MIN_WIDTH - 8));

    this.menuPosition.set({ top: Math.round(top), left: Math.round(left) });
    this.isOpen.set(true);
  }

  protected select(value: string): void {
    this.close();
    if (value !== this.current()) {
      this.statusChange.emit(value);
    }
  }
}
